import axios from "axios";

/**
 * Данные раздела «Маркетплейс сайтов»: шаблоны магазина, сайты клиентов, клиенты с заказами и оплатами.
 * Связки (клиент ↔ заказ ↔ сайт ↔ шаблон) и состояние хостинга собирает API — каждая страница получает свои данные
 * одним запросом к manage/marketplace-sites/... Магазин — Marketplace:Sites:ShopCompanyId в appsettings API.
 */

export const HOSTING_SKU = "hosting";

/** Статусы заказа (InstanceStates ядра), которые встречаются у заказов маркетплейса. */
export const ORDER_STATES: Record<number, { caption: string; color: string }> = {
  1: { caption: "Не оплачен", color: "grey lighten-2" },
  12: { caption: "Ждёт оплаты", color: "orange lighten-4" },
  100: { caption: "Оплачен", color: "green lighten-4" },
  9: { caption: "Отменён", color: "red lighten-4" },
};

export const PROVIDERS: Record<string, string> = {
  yookassa: "ЮKassa",
  balance: "С баланса",
  simulation: "Тестовая оплата",
};

/** Тип аккаунта клиента: «Частное лицо» или «Организация · название · ИНН» (из лицевого счёта). */
export function accountLabel(client: any): string {
  if (client?.accountType !== "company") {
    return "Частное лицо";
  }
  return ["Организация", client.companyName, client.inn ? `ИНН ${client.inn}` : ""].filter(Boolean).join(" · ");
}

/** templates — массив товаров; sites и clients — { sitesDomain, graceDays, frozenDays, items }. */
export async function loadMarketplace(part: "templates" | "sites" | "clients"): Promise<any> {
  const { data } = await axios.get(`/manage/marketplace-sites/${part}`);
  return data;
}

export function productImageUrl(product: any): string {
  const base = `${axios.defaults.baseURL ?? ""}`.replace(/\/$/, "");
  return product?.defaultImageId ? `${base}/objectimage/p${product.defaultImageId}.jpg` : "";
}

/** Подпись хостинга. Состояние (taken | active | grace | frozen | expired) считает API по SiteHostingPolicy ядра. */
export function hostingLabel(site: any): { caption: string; color: string } {
  switch (site.hostingState) {
    case "taken":
      return { caption: "Забран клиентом", color: "blue lighten-4" };
    case "active":
      return { caption: `Оплачен до ${formatDay(site.hostingPaidUntil)}`, color: "green lighten-4" };
    case "grace":
      return { caption: `Просрочен, работает ещё ${daysUntil(site.disableAt)} дн.`, color: "orange lighten-4" };
    case "frozen":
      return { caption: "Отключён за неоплату", color: "red lighten-4" };
    default:
      return { caption: "Ждёт удаления", color: "grey lighten-2" };
  }
}

function daysUntil(value: string): number {
  const day = new Date(value);
  const today = new Date();
  return Math.round((Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())
    - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
}

export function formatDay(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ru-RU");
}

export function formatMoney(value: number | null | undefined): string {
  return `${Math.round(Number(value ?? 0)).toLocaleString("ru-RU")} ₽`;
}
